import React from "react";
import PropTypes from "prop-types";
import { Dropdown, Input } from "@boozallen-uip/uswds-react-core";
import Tooltip from "../../../components/Tooltip.component";
import ExternalLinkIcon from "../../../components/ExternalLinkIcon.component";
import {
  FormField,
  FormFieldBody,
  FormFieldContainer,
  FormSectionContainer,
  FormSectionHeader,
  FormSectionHeaderContainer,
  FormSectionSubHeader,
} from "../../shared.styled";

export default function ProductInformationSection({
  product,
  categories,
  pscCodes,
  fedRampStatuses,
  clearFieldErrors,
  setProduct,
  getFieldError,
  renderInlineFieldError,
  FormFieldRequired,
  fieldIds,
  fieldContainerClassName,
  showSectionHeader,
  readOnly,
  onNameBlur,
}) {
  return (
    <FormSectionContainer>
      {showSectionHeader ? (
        <FormSectionHeaderContainer>
          <FormSectionHeader>Product Information</FormSectionHeader>
          <FormSectionSubHeader>The information below is specific to the product you are creating a listing for.</FormSectionSubHeader>
        </FormSectionHeaderContainer>
      ) : null}
      <FormFieldContainer className={fieldContainerClassName}>
        <FormFieldBody>
          <FormField>
            <Input id={fieldIds.name} value={product.name ?? ""} onChange={(e) => {
              if (readOnly) return;
              clearFieldErrors(["product.name"]);
              setProduct((prevProduct) => ({ ...prevProduct, name: e.target.value }));
            }} onBlur={onNameBlur} required readOnly={readOnly} disabled={readOnly}>
              Product name <FormFieldRequired />
            </Input>
            {renderInlineFieldError(getFieldError("product.name"))}
          </FormField>
          <FormField>
            <Input id={fieldIds.version} value={product.version ?? ""} onChange={(e) => {
              if (readOnly) return;
              setProduct((prevProduct) => ({ ...prevProduct, version: e.target.value }));
            }} readOnly={readOnly} disabled={readOnly}>
              Product version number or name &nbsp;
              <Tooltip label="Product version number or name">This refers to the product version the ACR was created for, and not the latest version of the product or the version submitted in this listing. To include a version for this specific product listing, please enter it above, under the &quot;Product Information&quot; section.</Tooltip>
            </Input>
          </FormField>
          <FormField className="col-start-1">
            <Dropdown id={fieldIds.category}
              options={categories.map((category) => category.title)}
              value={product.category || ""}
              onChange={e => {
                if (readOnly) return;
                clearFieldErrors(["product.category"]);
                setProduct((prevProduct) => ({ ...prevProduct, category: e.detail.value }));
              }}
              required
              disabled={readOnly}>
              Product category <FormFieldRequired />
            </Dropdown>
            {renderInlineFieldError(getFieldError("product.category"))}
          </FormField>
          <FormField>
            <Dropdown id={fieldIds.psc}
              options={pscCodes.map((pscCode) => pscCode.pscCode)}
              value={product.pscCode || ""}
              onChange={e => {
                if (readOnly) return;
                setProduct((prevProduct) => ({ ...prevProduct, pscCode: e.detail.value }));
              }}
              required
              disabled={readOnly}>
              Product and Service Code (PSC) &nbsp;
              <Tooltip label="Product and Service Code (PSC)">The Product and Service Codes (PSC) provide codes to describe products, services, and
                research and development (R&D) purchased by the federal government. For more information on the
                codes and how to use them, visit
                {" "}
                <a
                  href="https://www.acquisition.gov/psc-manual"
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent-cool"
                  aria-label="Open PSC manual on acquisition.gov (opens in new window)"
                >
                  Acquisition.gov PSC Manual
                  <span className="usa-sr-only"> (opens in new window)</span>
                  <ExternalLinkIcon />
                </a>
              </Tooltip>
            </Dropdown>
          </FormField>
          <FormField className="col-start-1 col-span-12">
            <Input
              id={fieldIds.description}
              value={product.description ?? ""}
              type="textarea"
              onChange={(e) => {
                if (readOnly) return;
                clearFieldErrors(["product.description"]);
                setProduct((prevProduct) => ({ ...prevProduct, description: e.target.value }));
              }}
              readOnly={readOnly}
              disabled={readOnly}
            >
              Product description <FormFieldRequired />
            </Input>
            {renderInlineFieldError(getFieldError("product.description"))}
          </FormField>
          <FormField className="col-start-1">
            <Dropdown id={fieldIds.fedRamp}
              options={fedRampStatuses}
              value={product.fedRampStatus || ""}
              onChange={e => {
                if (readOnly) return;
                setProduct((prevProduct) => ({ ...prevProduct, fedRampStatus: e.detail.value }));
              }}
              disabled={readOnly}
            >
              FedRAMP status (optional) &nbsp;
              <Tooltip label="FedRAMP status">The Federal Risk and Authorization Management Program (FedRAMP®) provides a standardized
                approach to security authorizations for Cloud Service Offerings. The purpose of this field is to
                indicate your product&apos;s FedRAMP status, as buyers often seek this information.
                {" "}
                <a
                  href="https://www.fedramp.gov"
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent-cool"
                  aria-label="Open fedramp.gov (opens in new window)"
                >
                  fedramp.gov
                  <span className="usa-sr-only"> (opens in new window)</span>
                  <ExternalLinkIcon />
                </a>
              </Tooltip>
            </Dropdown>
          </FormField>
        </FormFieldBody>
      </FormFieldContainer>
    </FormSectionContainer>
  );
}

ProductInformationSection.propTypes = {
  product: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired,
  pscCodes: PropTypes.array.isRequired,
  fedRampStatuses: PropTypes.array.isRequired,
  clearFieldErrors: PropTypes.func.isRequired,
  setProduct: PropTypes.func.isRequired,
  getFieldError: PropTypes.func.isRequired,
  renderInlineFieldError: PropTypes.func.isRequired,
  FormFieldRequired: PropTypes.elementType.isRequired,
  fieldIds: PropTypes.shape({
    name: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    psc: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    fedRamp: PropTypes.string.isRequired,
  }),
  fieldContainerClassName: PropTypes.string,
  showSectionHeader: PropTypes.bool,
  readOnly: PropTypes.bool,
  onNameBlur: PropTypes.func,
};

ProductInformationSection.defaultProps = {
  fieldIds: {
    name: "productName",
    version: "productVersion",
    category: "category",
    psc: "psc",
    description: "productDescription",
    fedRamp: "fedRamp",
  },
  fieldContainerClassName: "",
  showSectionHeader: true,
  readOnly: false,
  onNameBlur: undefined,
};
